import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox, Container, FormControlLabel, Grid, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import { Link } from 'react-router-dom';

import RegisterHeader from './RegisterHeader';
import User from '../../models/User';
import EditFields from '../common/EditFields';
import LoadingButton from '../common/LoadingButton';
import Footer from '../footer/Footer';

const useStyles = makeStyles((theme) => {
  return {
    description: {
      color: 'rgba(0, 0, 0, 0.38)',
      fontSize: '0.8rem',
      lineHeight: 1.8,
      marginBottom: '1rem',
    },
    detailsContainer: {
      paddingRight: '30px',
      textAlign: 'left',
    },
    fieldContainer: {
      display: 'block',
      margin: '24px 0px 16px',
      width: '100%',
      [theme.breakpoints.up('md')]: {
        display: 'inline-flex',
        width: 'auto',
      },
    },
    fieldPadding: {
      paddingRight: '50px',
    },
    fieldPaddingLeft: {
      paddingLeft: '0px',
      [theme.breakpoints.up('md')]: {
        paddingLeft: '30px',
      },
    },
    fieldPaddingRight: {
      paddingRight: '0px',
      [theme.breakpoints.up('md')]: {
        paddingRight: '30px',
      },
    },
    formTitle: {
      color: '#172B4D',
      fontSize: '30px',
      fontWeight: '500',
    },
    headerContainer: {
      width: '100%',
    },
    link: {
      color: theme.palette.primary.main,
    },
    loginHint: {
      color: 'rgba(0, 0, 0, 0.38)',
      fontSize: '14px',
      lineHeight: 1.8,
      marginBottom: '1rem',
    },
    logo: {
      display: 'flex',
      marginBottom: '3rem',
      textAlign: 'unset',
    },
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      maxWidth: '715px',
      padding: '16px 24px 50px',
    },
    parentContainer: {
      display: 'grid',
      minHeight: '100vh',
    },
    passwordContainer: {
      paddingLeft: '30px',
      textAlign: 'left',
    },
    selectLabels: {
      color: '#172B4D',
      fontSize: '16px',
      fontWeight: '500',
      marginBottom: '5px',
    },
    typographyLabel: {
      color: '#999999',
      fontSize: '12px',
      lineHeight: '16px',
      transform: 'translateY(2px)',
    },
  };
});

export default ({ onRegister, isRegistering$, errors, onChangeValues, values }) => {
  const [defaultData, setDefaultData] = useState(new User());
  const [validationErrors, setValidationErrors] = useState(errors);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [termsCheck, setTermsCheck] = useState(false);

  useEffect(() => {
    setIsButtonEnabled(values?.isButtonEnabled ? values.isButtonEnabled() : false);
    setTermsCheck(values.termsCheck);
  }, [values]);

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'));
  useEffect(() => {
    setValidationErrors(errors);
  }, [errors]);
  const classes = useStyles();

  const detailsFields = useMemo(
    () => [
      {
        containerClass: `${classes.inlineFlex} ${classes.fieldPaddingRight} ${classes.fieldContainer}`,
        label: 'Name',
        labelPosition: 'top',
        name: 'name',
        placeholder: 'Full name',
        type: 'text',
        width: isDesktop ? '50%' : '100%',
      },
      {
        containerClass: `${classes.inlineFlex} ${classes.fieldPaddingLeft} ${classes.fieldContainer}`,
        label: 'Preferred Name',
        labelPosition: 'top',
        name: 'preferredName',
        placeholder: 'Preferred',
        type: 'text',
        width: isDesktop ? '50%' : '100%',
      },
      {
        containerClass: `${classes.inlineFlex} ${classes.fieldPaddingRight} ${classes.fieldContainer}`,
        label: 'Telephone',
        labelPosition: 'top',
        name: 'phoneNumber',
        placeholder: '+27828501512',
        type: 'phone',
        width: isDesktop ? '50%' : '100%',
      },
      {
        containerClass: `${classes.inlineFlex} ${classes.fieldPaddingLeft} ${classes.fieldContainer}`,
        label: 'Date of Birth',
        labelPosition: 'top',
        name: 'dateOfBirth',
        placeholder: 'MM/DD/YYYY',
        type: 'date',
        width: isDesktop ? '50%' : '100%',
      },
      {
        containerClass: `${classes.inlineFlex} ${classes.fieldPaddingRight} ${classes.fieldContainer}`,
        label: 'Email Address',
        labelPosition: 'top',
        name: 'email',
        placeholder: 'email@address.com',
        type: 'text',
        width: isDesktop ? '50%' : '100%',
      },
      {
        containerClass: `${classes.inlineFlex} ${classes.fieldPaddingLeft} ${classes.fieldContainer}`,
        items: [
          { label: 'Female', value: 'female' },
          { label: 'Male', value: 'male' },
          { label: 'Other', value: 'other' },
        ],
        label: 'Gender',
        labelPosition: 'top',
        name: 'gender',
        placeholder: 'Select',
        type: 'select',
        width: isDesktop ? '50%' : '100%',
      },
      {
        containerClass: `${classes.inlineFlex} ${classes.fieldPaddingRight} ${classes.fieldContainer}`,
        hidePasswordIcon: true,
        label: 'Password',
        labelPosition: 'top',
        name: 'password',
        placeholder: '***********',
        type: 'password',
        width: isDesktop ? '50%' : '100%',
      },
      {
        containerClass: `${classes.inlineFlex} ${classes.fieldPaddingLeft} ${classes.fieldContainer}`,
        hidePasswordIcon: true,
        label: 'Re-enter Password',
        labelPosition: 'top',
        name: 'confirmPassword',
        placeholder: '***********',
        type: 'password',
        width: isDesktop ? '50%' : '100%',
      },
    ],
    [isDesktop]
  );

  const detailsFieldRef = useRef();

  const onRegisterClick = async () => {
    const details = await detailsFieldRef.current.getValidData();

    if (details) {
      onRegister(details);
    }
  };

  const handleSetExternalError = (fieldName, value) => {
    const currentError = { ...validationErrors };
    if (currentError[fieldName] && !value) delete currentError[fieldName];
    setValidationErrors(currentError);
  };

  return (
    <div className={classes.parentContainer}>
      <RegisterHeader />

      <Container className={classes.mainContainer} maxWidth={false}>
        <div>
          <div>
            <Grid container>
              <Grid item component="div" xs={12}>
                <Typography variant="h4" className={`${classes.formTitle}`} align="left">
                  Register Account
                </Typography>
              </Grid>
            </Grid>
          </div>
          <Grid container>
            <Grid item xs={12}>
              <EditFields
                data={defaultData}
                fields={detailsFields}
                useLabel
                ref={detailsFieldRef}
                labelPosition="top"
                labelClass={classes.selectLabels}
                validate={User.validateCreate}
                validationErrors={validationErrors}
                onSetExternalError={handleSetExternalError}
              />
            </Grid>

            <Grid item xs={12}>
              <div className="text-left mt-1">
                <FormControlLabel
                  classes={{ root: 'mediphany-formcontrol-root' }}
                  control={
                    <Checkbox
                      classes={{ root: 'mediphany-checkbox-root' }}
                      value={termsCheck}
                      onChange={(event) => onChangeValues(event.target.checked, 'termsCheck')}
                      name="checkedB"
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography className={`${classes.typographyLabel} text-left`}>
                      I have read, understand and agree to Mediphany’s&nbsp;
                      <a href="https://mediphany.com/terms-and-conditions/" className={classes.link} target="_blank" rel="noreferrer">
                        Terms of Service
                      </a>
                      &nbsp;and&nbsp;
                      <a href="https://mediphany.com/privacy-policy/" className={classes.link} target="_blank" rel="noreferrer">
                        Privacy Policy
                      </a>
                      .
                    </Typography>
                  }
                />
              </div>
            </Grid>

            <Grid container item xs={12}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" className={`${classes.fieldPaddingRight} ${classes.fieldContainer} ${classes.loginHint} body-copy`} align="left">
                  Note that your password needs to contain at least one capital letter and one numeric character.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} className={classes.fieldPaddingLeft}>
                <LoadingButton
                  color="primary"
                  style={{ marginTop: '1rem', width: '100%' }}
                  variant="contained"
                  size="large"
                  onClick={onRegisterClick}
                  disabled={!isButtonEnabled}
                  loading$={isRegistering$}>
                  Register
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Container>

      <Footer />
    </div>
  );
};
